// ExpensesStep.js — Mortgage Matrix Broker CRM
// v5.2 — FIX: save expenses into crmData.expenses (so Lambda can read body.expenses)
// -----------------------------------------------------------------------------
// ✅ Same PropertyStep / AssetsLiabilities look
// ✅ Thousands separators for Amount
// ✅ Robust hydrate from: data.expenses OR data (array legacy)
// ✅ Never overwrites user edits after first external hydrate while focused/touched
// ✅ Commits to parent on every USER change
//
// IMPORTANT:
// This component now calls: updateCrmData({ expenses: [...] })
// so your sendToMercury receives: body.expenses

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"

const EXPENSE_TYPES = [
  "Board",
  "Childcare",
  "Child & Spouse Maintenance",
  "Clothing & Personal Care",
  "General Insurance",
  "Groceries",
  "Medical & Health",
  "Personal Insurance",
  "Primary Residence Costs",
  "Public or Government Primary & Secondary Education",
  "Recreation & Entertainment",
  "Rent",
  "Telephone, Internet, Pay TV & Media Streaming",
  "Transport",
  "Other",
]

const FREQUENCIES = ["Weekly", "Fortnightly", "Monthly", "Annual"]

export default function ExpensesStep({ data, updateCrmData }) {
  // -----------------------------
  // Normalise incoming shape
  // -----------------------------
  const getExternalExpenses = useCallback((d) => {
    if (Array.isArray(d)) return d // legacy
    if (d && typeof d === "object" && Array.isArray(d.expenses)) return d.expenses
    return []
  }, [])

  // -----------------------------
  // Local State
  // -----------------------------
  const [expenses, setExpenses] = useState(() => getExternalExpenses(data))

  // Prevent “shake” from repeated external hydrates while user is editing
  const didExternalHydrateRef = useRef(false)
  const touchedRef = useRef(false)
  const isFocusedRef = useRef(false)

  const stableStringify = (obj) => {
    try {
      return JSON.stringify(obj ?? null)
    } catch {
      return ""
    }
  }

  const lastExternalJSONRef = useRef(stableStringify(getExternalExpenses(data)))

  // -----------------------------
  // External sync down (safe)
  // -----------------------------
  useEffect(() => {
    const ext = getExternalExpenses(data)
    const nextJSON = stableStringify(ext)
    const prevJSON = lastExternalJSONRef.current

    if (prevJSON === nextJSON) return

    // Ignore “bounce” updates while focused
    if (isFocusedRef.current) return
    // After user touches, ignore further external hydrates
    if (touchedRef.current && didExternalHydrateRef.current) return

    lastExternalJSONRef.current = nextJSON
    setExpenses(ext)

    if (Array.isArray(ext) && ext.length) didExternalHydrateRef.current = true
  }, [data, getExternalExpenses])

  // -----------------------------
  // Commit helper (USER changes)
  // -----------------------------
  const commit = useCallback(
    (nextExpenses) => {
      if (typeof updateCrmData === "function") {
        // ✅ IMPORTANT: this writes to crmData.expenses
        updateCrmData({ expenses: nextExpenses })
      }
    },
    [updateCrmData]
  )

  // -----------------------------
  // Helpers
  // -----------------------------
  const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "")

  const formatThousands = (v) => {
    const raw = digitsOnly(v)
    if (!raw) return ""
    return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  const markTouched = () => {
    touchedRef.current = true
  }

  // -----------------------------
  // Mutators
  // -----------------------------
  const addExpense = () => {
    markTouched()
    setExpenses((prev) => {
      const next = [...prev, { type: "", amount: "", frequency: "Monthly" }]
      commit(next)
      return next
    })
  }

  const removeExpense = (index) => {
    markTouched()
    setExpenses((prev) => {
      const next = prev.filter((_, i) => i !== index)
      commit(next)
      return next
    })
  }

  const updateExpense = (index, field, value) => {
    markTouched()
    setExpenses((prev) => {
      const copy = [...prev]
      copy[index] = { ...copy[index], [field]: value }
      commit(copy)
      return copy
    })
  }

  // -----------------------------
  // UI (PropertyStep look)
  // -----------------------------
  const ui = useMemo(() => {
    const ACCENT = "#4EDCF6"
    const DARK = "#111827"
    const MUTED = "#6B7280"
    const BORDER = "#E5E7EB"
    const INPUT_BORDER = "#334155"
    const CARD_BG = "#E6FBFF"
    const SHADOW = "0 10px 24px rgba(78,220,246,0.22)"

    return {
      page: { padding: 8, maxWidth: 920 },

      h2: { marginBottom: 10, fontSize: 26, fontWeight: 700, color: DARK },

      intro: { marginBottom: 12, color: MUTED, fontWeight: 500 },

      card: {
        background: CARD_BG,
        padding: 18,
        borderRadius: 18,
        border: `3px solid ${ACCENT}`,
        boxShadow: SHADOW,
      },

      inner: {
        background: "#fff",
        padding: 14,
        borderRadius: 16,
        border: `2px solid ${BORDER}`,
      },

      sectionTitle: {
        margin: 0,
        marginBottom: 10,
        fontSize: 20,
        fontWeight: 700,
        color: DARK,
      },

      label: {
        display: "block",
        marginBottom: 6,
        fontSize: 15,
        fontWeight: 600,
        color: DARK,
      },

      grid: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr 1fr auto",
        gap: 12,
        alignItems: "end",
        marginBottom: 12,
      },

      input: {
        width: "100%",
        height: 50,
        padding: "10px 12px",
        borderRadius: 14,
        border: `2px solid ${INPUT_BORDER}`,
        background: "#fff",
        fontSize: 16,
        fontWeight: 600,
        outline: "none",
        minWidth: 0,
      },

      adornWrap: { position: "relative", height: 50, width: "100%" },

      adornLeft: {
        position: "absolute",
        left: 14,
        top: "50%",
        transform: "translateY(-50%)",
        fontSize: 16,
        fontWeight: 700,
        color: DARK,
        opacity: 0.8,
        pointerEvents: "none",
      },

      inputWithDollar: {
        width: "100%",
        height: 50,
        padding: "10px 12px 10px 34px",
        borderRadius: 14,
        border: `2px solid ${INPUT_BORDER}`,
        background: "#fff",
        fontSize: 16,
        fontWeight: 600,
        outline: "none",
        minWidth: 0,
      },

      addBtn: {
        padding: "12px 18px",
        background: ACCENT,
        border: "none",
        borderRadius: 12,
        cursor: "pointer",
        fontWeight: 600,
        boxShadow: SHADOW,
        marginTop: 10,
      },

      removeBtn: {
        background: "transparent",
        border: "none",
        color: "#DC2626",
        fontSize: 16,
        fontWeight: 600,
        cursor: "pointer",
        height: 50,
        display: "inline-flex",
        alignItems: "center",
      },

      helper: {
        marginTop: 10,
        fontSize: 13,
        color: MUTED,
        fontWeight: 500,
      },

      empty: {
        padding: 14,
        borderRadius: 14,
        border: `2px dashed ${BORDER}`,
        background: "#FAFAFA",
        color: MUTED,
        fontWeight: 600,
        marginBottom: 12,
      },
    }
  }, [])

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div
      style={ui.page}
      onFocusCapture={() => {
        isFocusedRef.current = true
      }}
      onBlurCapture={() => {
        isFocusedRef.current = false
      }}
    >
      <h2 style={ui.h2}>Expenses</h2>
      <p style={ui.intro}>
        Capture the clients’ ongoing <strong>living expenses</strong>.
      </p>

      <div style={ui.card}>
        <h3 style={ui.sectionTitle}>Living Expenses</h3>

        <div style={ui.inner}>
          {expenses.length === 0 && <div style={ui.empty}>No expenses added yet. Click “+ Add Expense” to begin.</div>}

          {expenses.map((exp, index) => (
            <div key={index} style={ui.grid}>
              <div>
                <label style={ui.label}>Expense category</label>
                <select
                  style={ui.input}
                  value={exp.type || ""}
                  onChange={(e) => updateExpense(index, "type", e.target.value)}
                >
                  <option value="">Select category</option>
                  {EXPENSE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={ui.label}>Amount</label>
                <div style={ui.adornWrap}>
                  <span style={ui.adornLeft}>$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatThousands(exp.amount ?? "")}
                    onChange={(e) => updateExpense(index, "amount", digitsOnly(e.target.value))}
                    style={ui.inputWithDollar}
                  />
                </div>
              </div>

              <div>
                <label style={ui.label}>Frequency</label>
                <select
                  style={ui.input}
                  value={exp.frequency || "Monthly"}
                  onChange={(e) => updateExpense(index, "frequency", e.target.value)}
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              <button type="button" onClick={() => removeExpense(index)} style={ui.removeBtn}>
                ❌ Remove
              </button>
            </div>
          ))}

          <button type="button" onClick={addExpense} style={ui.addBtn}>
            + Add Expense
          </button>

          <div style={ui.helper}>Tip: add only what you know — we can refine figures during assessment.</div>
        </div>
      </div>
    </div>
  )
}